import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/ui/Button.jsx";
import ReportExportBar from "../components/report/ReportExportBar.jsx";
import { ReportPageFooter, ReportPageHeader } from "../components/report/ReportPageChrome.jsx";
import { formatComplianceScopeDisplay } from "../compliance/complianceScopeDisplay.js";
import { topRiskDisplayLabel } from "../utils/topRiskLabel.js";
import { formatOpenSeverityCountLine } from "../utils/severityDisplay.js";
import ThreatContextBlock from "../components/ThreatContextBlock.jsx";
import ActionableSummaryPanel from "../components/remediation/ActionableSummaryPanel.jsx";
import FindingRemediationBlock from "../components/remediation/FindingRemediationBlock.jsx";
import { getProviderMetaForProject, resolveProviderId } from "../utils/providerContext.js";
import { useReportSnapshot, formatReportDateTime } from "../hooks/useReportSnapshot.js";
import { resolveReportTenant } from "../utils/reportTenant.js";

function providerMetaFrom(projectOrSnapshot) {
  return getProviderMetaForProject(
    projectOrSnapshot && typeof projectOrSnapshot === "object" ? projectOrSnapshot : null,
  );
}

function unableToEvaluateIntro(projectOrSnapshot) {
  return providerMetaFrom(projectOrSnapshot).unableToEvaluateIntro;
}

function reportMethodologyLine(projectOrSnapshot) {
  return providerMetaFrom(projectOrSnapshot).methodologyLine;
}

function reportExecutiveFallback(projectOrSnapshot) {
  return providerMetaFrom(projectOrSnapshot).executiveFallback;
}

function formatDateTime(iso) {
  return formatReportDateTime(iso);
}

function topRisks(findings) {
  const crit = Array.isArray(findings?.critical) ? findings.critical : [];
  const warn = Array.isArray(findings?.warnings) ? findings.warnings : [];
  const items = [...crit, ...warn];
  const rank = (sev) => {
    const s = String(sev || "").toLowerCase();
    if (s === "critical") return 4;
    if (s === "high") return 3;
    if (s === "medium") return 2;
    if (s === "low") return 1;
    if (s === "info") return 0;
    return 0;
  };
  items.sort((a, b) => {
    const sa = rank(a?.severity);
    const sb = rank(b?.severity);
    if (sb !== sa) return sb - sa;
    const ra = Number(a?.relevanceScore) || 0;
    const rb = Number(b?.relevanceScore) || 0;
    if (rb !== ra) return rb - ra;
    return String(a?.ruleId || "").localeCompare(String(b?.ruleId || ""));
  });
  return items.slice(0, 3);
}

export default function ReportView() {
  const { projectId, snapshotId } = useParams();
  const navigate = useNavigate();
  const { project, snapshots, snapshot, loading, error } = useReportSnapshot(projectId, snapshotId);
  const [execSummary, setExecSummary] = useState("");
  const [execSummaryBusy, setExecSummaryBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const pid = projectId;
    const sid = snapshot?.snapshotId;
    if (!pid || !sid) return;
    setExecSummaryBusy(true);
    setExecSummary("");
    fetch(
      `/api/projects/${encodeURIComponent(pid)}/snapshots/${encodeURIComponent(sid)}/executive-summary`,
      { credentials: "include" },
    )
      .then((r) => r.json().catch(() => ({})))
      .then((j) => {
        if (!cancelled) setExecSummary(typeof j?.summary === "string" ? j.summary : "");
      })
      .catch(() => {
        if (!cancelled) setExecSummary("");
      })
      .finally(() => {
        if (!cancelled) setExecSummaryBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, snapshot?.snapshotId]);

  const findings = snapshot?.findings;
  const summary = snapshot?.summary;
  const risks = useMemo(() => topRisks(findings), [findings]);
  const incomplete = Boolean(summary?.incomplete);
  const providerId = resolveProviderId(project || snapshot);
  const activeSnapshotId = snapshot?.snapshotId || snapshotId;

  const orgName = project?.org?.name || "Project";
  const env = project?.org?.environment || "";
  const tenant = resolveReportTenant(project, snapshot);
  const scope = formatComplianceScopeDisplay(
    Array.isArray(snapshot?.summary?.compliance?.selectedFrameworks)
      ? snapshot.summary.compliance.selectedFrameworks
      : Array.isArray(project?.complianceFrameworks)
        ? project.complianceFrameworks
        : null,
    providerId,
  );

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--idaxis-print-bg, #fff)", color: "var(--idaxis-print-text, #0b1b34)" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px" }}>Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--idaxis-print-bg, #fff)", color: "var(--idaxis-print-text, #0b1b34)" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px" }}>
          <p style={{ color: "#b91c1c", marginBottom: 16 }}>{error}</p>
          <Button variant="secondary" onClick={() => navigate(`/project/${encodeURIComponent(projectId)}`)}>
            ← Back to workspace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-theme="light"
      style={{
        minHeight: "100vh",
        background: "var(--idaxis-print-bg, #fff)",
        color: "var(--idaxis-print-text, #0b1b34)",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 24px 48px" }}>
        <div
          className="no-print"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            background: "var(--idaxis-print-bg, #fff)",
            paddingBottom: 8,
            marginBottom: 8,
            borderBottom: "1px solid var(--idaxis-print-border, #e5e7eb)",
          }}
        >
          <ReportPageHeader
            projectId={projectId}
            projectName={orgName}
            snapshotTakenAt={snapshot?.takenAt || snapshot?.takenAtIso}
            variant="light"
          />
          <div style={{ marginTop: 12 }}>
            <ReportExportBar projectId={projectId} snapshotId={activeSnapshotId} activeDoc="audit" variant="light" />
          </div>
        </div>

        <div style={{ paddingTop: 8 }}>
          <h1 style={{ fontSize: 26, margin: 0, fontWeight: 800, letterSpacing: "-0.02em" }}>
            Identity Posture Assessment
          </h1>
          <p style={{ marginTop: 8, marginBottom: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            {orgName}{env ? ` — ${env}` : ""}
          </p>
          <p style={{ marginTop: 6, marginBottom: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            Tenant: <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>{tenant}</span>
          </p>
          <p style={{ marginTop: 6, marginBottom: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            Compliance scope: {scope}
          </p>
        </div>

        {incomplete ? (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid var(--idaxis-print-border, #e5e7eb)",
              borderLeft: "4px solid #b45309",
              color: "var(--idaxis-print-text, #0b1b34)",
            }}
          >
            <strong>Assessment incomplete.</strong>{" "}
            <span style={{ color: "var(--idaxis-print-muted, #334155)" }}>
              Some data was unavailable during collection; findings may be partial.
            </span>
          </div>
        ) : null}

        <section style={{ marginTop: 26 }}>
          <h2 style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", margin: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            Executive Summary
          </h2>
          <div style={{ marginTop: 10, padding: "14px 16px", border: "1px solid var(--idaxis-print-border, #e5e7eb)", borderRadius: 10 }}>
            {execSummaryBusy ? (
              <p style={{ margin: 0, lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
                Generating executive summary…
              </p>
            ) : execSummary ? (
              <p style={{ margin: 0, lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
                {execSummary}
              </p>
            ) : (
              <p style={{ margin: 0, lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
                {reportExecutiveFallback(project || snapshot)}
              </p>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <h3 style={{ fontSize: 12, margin: 0, fontWeight: 800 }}>Top 3 risks</h3>
            {risks.length === 0 ? (
              <p style={{ marginTop: 8, color: "var(--idaxis-print-muted, #334155)" }}>No critical or high risks detected in this snapshot.</p>
            ) : (
              <ol style={{ marginTop: 8, paddingLeft: 18 }}>
                {risks.map((r) => (
                  <li key={r.id || r.ruleId || r.title} style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 700 }}>{topRiskDisplayLabel(r)}</span>{" "}
                    <span style={{ color: "var(--idaxis-print-muted, #334155)" }}>
                      ({r.ruleId || "—"})
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <ActionableSummaryPanel snapshot={snapshot} providerId={providerId} variant="report" />
        </section>

        <section style={{ marginTop: 26 }}>
          <h2 style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", margin: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            Findings
          </h2>
          <p style={{ marginTop: 8, color: "var(--idaxis-print-muted, #334155)" }}>
            {formatOpenSeverityCountLine(summary, {
              inconclusive:
                summary?.inconclusiveCount ??
                (Array.isArray(findings?.inconclusive) ? findings.inconclusive.length : 0),
              healthy: summary?.healthyCount ?? 0,
            })}
          </p>

          <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
            {(Array.isArray(findings?.critical) ? findings.critical : []).map((f) => (
              <FindingCard key={f.id || f.ruleId || f.title} finding={f} providerId={providerId} />
            ))}
            {(Array.isArray(findings?.warnings) ? findings.warnings : []).map((f) => (
              <FindingCard key={f.id || f.ruleId || f.title} finding={f} providerId={providerId} />
            ))}
          </div>

          {(Array.isArray(findings?.inconclusive) ? findings.inconclusive : []).length > 0 ? (
            <section style={{ marginTop: 22 }}>
              <h3
                style={{
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  margin: "0 0 10px",
                  fontWeight: 800,
                  color: "var(--idaxis-print-muted, #334155)",
                }}
              >
                UNABLE TO EVALUATE ({(Array.isArray(findings?.inconclusive) ? findings.inconclusive : []).length} rules)
              </h3>
              <p style={{ margin: "0 0 10px", lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
                These rules could not produce a determination due to missing data or token scope. They are not security findings.
              </p>
              <p style={{ margin: "0 0 14px", lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
                {unableToEvaluateIntro(project || snapshot)}
              </p>
              <div style={{ display: "grid", gap: 12 }}>
                {(Array.isArray(findings?.inconclusive) ? findings.inconclusive : []).map((f) => (
                  <InconclusiveFindingCard key={f.id || f.ruleId || f.title} finding={f} />
                ))}
              </div>
            </section>
          ) : null}

          <div style={{ marginTop: 14 }}>
            <details>
              <summary style={{ cursor: "pointer", color: "var(--idaxis-print-muted, #334155)" }}>
                Healthy findings ({Array.isArray(findings?.healthy) ? findings.healthy.length : 0})
              </summary>
              <ul style={{ marginTop: 10, paddingLeft: 18 }}>
                {(Array.isArray(findings?.healthy) ? findings.healthy : []).slice(0, 40).map((f) => (
                  <li key={f.id || f.ruleId || f.title} style={{ marginBottom: 4 }}>
                    {f.title}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </section>

        <section style={{ marginTop: 26 }}>
          <h2 style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", margin: 0, color: "var(--idaxis-print-muted, #334155)" }}>
            Appendix
          </h2>
          <div style={{ marginTop: 10, padding: "14px 16px", border: "1px solid var(--idaxis-print-border, #e5e7eb)", borderRadius: 10 }}>
            <p style={{ margin: 0, color: "var(--idaxis-print-muted, #334155)", lineHeight: 1.6 }}>
              {reportMethodologyLine(project || snapshot)}
            </p>
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--idaxis-print-muted, #334155)" }}>
              <div>Rubric version: {snapshot?.rubricVersion || "—"}</div>
              <div>idwiz version: {snapshot?.idwizVersion || "—"}</div>
              <div>Tenant snapshot: {snapshot?.snapshotId || "—"}</div>
            </div>
          </div>
        </section>

        <ReportPageFooter
          projectId={projectId}
          projectName={orgName}
          providerLabel={providerMetaFrom(project || snapshot)?.displayName || providerId}
          snapshots={snapshots}
          activeSnapshotId={snapshot?.snapshotId || snapshotId}
          onSnapshotChange={(sid) =>
            navigate(`/project/${encodeURIComponent(projectId)}/report/${encodeURIComponent(sid)}`)
          }
          variant="light"
        />
      </div>
    </div>
  );
}

function FindingCard({ finding, providerId }) {
  const sev = String(finding?.severity || "").toLowerCase();
  const color =
    sev === "critical" ? "#b91c1c" : sev === "high" ? "#b45309" : sev === "medium" ? "#b45309" : "#334155";
  return (
    <article
      style={{
        border: "1px solid var(--idaxis-print-border, #e5e7eb)",
        borderRadius: 12,
        padding: "12px 14px",
        pageBreakInside: "avoid",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontWeight: 800 }}>{finding?.title || "Finding"}</div>
        <div style={{ fontSize: 12, color }}>{String(finding?.severity || "").toUpperCase()}</div>
      </div>
      {finding?.detail ? (
        <p style={{ marginTop: 8, marginBottom: 0, lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
          {finding.detail}
        </p>
      ) : null}
      <FindingRemediationBlock ruleId={finding?.ruleId} providerId={providerId} variant="report" />
      <ThreatContextBlock references={finding?.threatReferences} variant="report" />
      <div style={{ marginTop: 10, fontSize: 12, color: "var(--idaxis-print-muted, #334155)" }}>
        Rule:{" "}
        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
          {finding?.ruleId || "—"}
        </span>
      </div>
    </article>
  );
}

function InconclusiveFindingCard({ finding }) {
  return (
    <article
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: 12,
        padding: "12px 14px",
        pageBreakInside: "avoid",
        background: "#f8fafc",
      }}
    >
      <div style={{ fontWeight: 700 }}>{finding?.title || "Rule"}</div>
      {finding?.detail ? (
        <p style={{ marginTop: 8, marginBottom: 0, lineHeight: 1.6, color: "var(--idaxis-print-muted, #334155)" }}>
          {finding.detail}
        </p>
      ) : null}
      {finding?.unblockHint ? (
        <p style={{ marginTop: 8, marginBottom: 0, lineHeight: 1.55, fontSize: 13, color: "#64748b" }}>
          <span style={{ fontStyle: "italic" }}>What would unblock:</span> {finding.unblockHint}
        </p>
      ) : null}
      <ThreatContextBlock references={finding?.threatReferences} variant="report" />
      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        Rule:{" "}
        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
          {finding?.ruleId || "—"}
        </span>
      </div>
    </article>
  );
}

